/**
 * Modal for capturing a reason when putting a PO line on hold.
 *
 * Follows the same pattern as AcceptDateModal / DeclineDateModal.
 */
import { useState } from 'react';
import { X, PauseCircle, Loader2 } from 'lucide-react';
import { Card } from '../ui/card';
import {
  HoldNextActionChoice, defaultHoldNextAction, type HoldNextAction,
} from './HoldNextActionChoice';

interface HoldReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** G7: the nomination rides with the reason — one question, one submit.
   *  `nextAction` is absent when the placer is not a side of the deal. */
  onSubmit: (reason: string, nextAction?: HoldNextAction) => Promise<void>;
  lineNumber: number | string;
  partNumber: string;
  /** The side placing the hold — the "me" in the nomination options. */
  /**
   * The side placing the hold, when it IS one. A platform operator or a logistics
   * user is neither, and has no side to hand the hold to: the question is not
   * asked, nothing is nominated, and their hold stays theirs (F-J, blind stage 2,
   * round 2 — the modal knew two sides, so "With me" from an operator sent
   * `customer` and recorded a handover they had not made).
   */
  placerSide?: 'customer' | 'supplier';
}

export function HoldReasonModal({
  isOpen,
  onClose,
  onSubmit,
  lineNumber,
  partNumber,
  placerSide,
}: HoldReasonModalProps) {
  const [reason, setReason] = useState('');
  const [nextAction, setNextAction] = useState<HoldNextAction>(
    defaultHoldNextAction(placerSide || 'customer'));
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(reason.trim(), placerSide ? nextAction : undefined);
      setReason('');
      setNextAction(defaultHoldNextAction(placerSide || 'customer'));
      onClose();
    } catch {
      // error toast handled by caller
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold">Put Line On Hold</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-sm text-muted-foreground">
            Line <span className="font-medium text-foreground">{lineNumber}</span>
            {partNumber && <> &mdash; Part <span className="font-medium text-foreground">{partNumber}</span></>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for putting this line on hold..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              rows={3}
              required
              autoFocus
            />
          </div>

          {placerSide ? (
            <HoldNextActionChoice
              placerSide={placerSide}
              value={nextAction}
              onChange={setNextAction}
              disabled={submitting}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              This hold stays with you — neither the customer nor the supplier can
              take it off.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <PauseCircle className="h-4 w-4" />
                  Put On Hold
                </>
              )}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
